import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import {
  appCheckSiteKey,
  emulatorHost,
  firebaseConfig,
  functionsRegion,
  useEmulators,
} from './config';

let app: FirebaseApp | undefined;
let authInstance: Auth | undefined;
let firestoreInstance: Firestore | undefined;
let functionsInstance: Functions | undefined;

function initialize(): FirebaseApp {
  if (app) return app;

  app = initializeApp(firebaseConfig);

  authInstance = getAuth(app);
  firestoreInstance = getFirestore(app);
  functionsInstance = getFunctions(app, functionsRegion);

  if (useEmulators) {
    connectAuthEmulator(authInstance, `http://${emulatorHost}:9099`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(firestoreInstance, emulatorHost, 8080);
    connectFunctionsEmulator(functionsInstance, emulatorHost, 5001);
  } else if (appCheckSiteKey) {
    // App Check attests that requests come from this app, blunting abuse of the
    // anonymous-auth surface. It is not a substitute for the authorization
    // checks in Cloud Functions and Security Rules — those still run.
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  return app;
}

export function firebaseApp(): FirebaseApp {
  return initialize();
}

export function firebaseAuth(): Auth {
  initialize();
  return authInstance!;
}

export function firestore(): Firestore {
  initialize();
  return firestoreInstance!;
}

export function firebaseFunctions(): Functions {
  initialize();
  return functionsInstance!;
}
